import { useState } from 'react'
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import useAuth from '@/hooks/useAuth'
import { useCreateNewUserActionMutation, useGetUserActionsQuery } from "@/features/userActions/userActionsApiSlice"
import { useGetActionTypesQuery } from "@/features/actionTypes/actionTypesApiSlice"
import { useGetUserTrailSessionExecutionsQuery } from "@/features/userTrailSessions/userTrailSessionExecutionsApiSlice"
import { useGetSessionsQuery } from "@/features/sessions/sessionsApiSlice"
import { useGetUsersQuery } from "@/features/users/usersApiSlice"

//07 Apr 2025
import { useGetUserMissionClientsQuery } from '@/features/userMissions/userMissionClientsApiSlice'
import { useGetUserMissionOrgsQuery } from '@/features/userMissions/userMissionOrgsApiSlice'
import { useGetUserMissionPartnersQuery } from '@/features/userMissions/userMissionPartnersApiSlice'
import { useGetUserMissionSelfsQuery } from '@/features/userMissions/userMissionSelfsApiSlice'
import { useGetUserMissionTeamsQuery } from '@/features/userMissions/userMissionTeamsApiSlice'

import { useGetUserVisionClientsQuery } from '@/features/userVisions/userVisionClientsApiSlice'
import { useGetUserVisionOrgsQuery } from '@/features/userVisions/userVisionOrgsApiSlice'
import { useGetUserVisionPartnersQuery } from '@/features/userVisions/userVisionPartnersApiSlice'
import { useGetUserVisionSelfsQuery } from '@/features/userVisions/userVisionSelfsApiSlice'
import { useGetUserVisionTeamsQuery } from '@/features/userVisions/userVisionTeamsApiSlice'

import { useGetUserPlanClientsQuery } from '@/features/userPlans/userPlanClientsApiSlice'
import { useGetUserPlanOrgsQuery } from '@/features/userPlans/userPlanOrgsApiSlice'
import { useGetUserPlanPartnersQuery } from '@/features/userPlans/userPlanPartnersApiSlice'
import { useGetUserPlanSelfsQuery } from '@/features/userPlans/userPlanSelfsApiSlice'
import { useGetUserPlanTeamsQuery } from '@/features/userPlans/userPlanTeamsApiSlice'

const EtCreateFollowUpUserActionComp = ({ 
  onClose = () => {}, 
  refetchUserActions = () => {},
  defaultTrailId = '', 
  defaultSessionId = '',
  calendarMeetingId: propCalendarMeetingId = null,    // ✅ 22 Jun 2025 New
  sourceConversation: propSourceConversation = null,  // ✅ 22 Jun 2025 New
}) => {

  // Author - Atul Pandey
  // First Created - 22 Jun 2025
  // Purpose - To create a follow up user action from Trinity Power up companion including capturing a new follow up action resulting from a meeting or conversation
    
  
  // Original Update Trail
  // ----------------------
  // First Created: 06 Apr 2025  
  // Author: Atul Pandey  
  // Purpose: Create a standalone component to add a new user action from Trinity Web Landing  
  // Updated - 07 Apr 2025 - Added queries for User Visions, Missions and Plans
  // Updated - 17 May 2025 - Added defaultTrailId = '', defaultSessionId = '' to reuse the modal for user sessions

  // 1️⃣ Auth and Local User Info
  const { username } = useAuth()
  console.log('username', username)
  console.log('calendarMeetingId', propCalendarMeetingId)

  // 2️⃣ State Variables for Form Inputs
  // const [selectedTrailId, setSelectedTrailId] = useState('')
  // const [selectedSessionId, setSelectedSessionId] = useState('')
  const [selectedTrailId, setSelectedTrailId] = useState(defaultTrailId)          // ✅ 17 May 2025
  const [selectedSessionId, setSelectedSessionId] = useState(defaultSessionId)    // ✅ 17 May 2025
  const [actionName, setActionName] = useState('')
  const [actionDesc, setActionDesc] = useState('')
  const [selectedActionTypeId, setSelectedActionTypeId] = useState('')
  const [completionDate, setCompletionDate] = useState('')

  // 07 Apr 2025 - Additional states for MyMissions trail support
  const [selectedFocus, setSelectedFocus] = useState('');
  const [selectedVisionId, setSelectedVisionId] = useState('');
  const [selectedMissionId, setSelectedMissionId] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState('');

  // 22 Jun 2025 - CalendarMeetingId , sourceConversation
  const [calendarMeetingId, setCalendarMeetingId] = useState(propCalendarMeetingId);
  const [sourceConversation, setSourceConversation] = useState(propSourceConversation);

  // 22 Jun 2025 - Local fallback states for form-captured inputs
  const [localCalendarMeetingId, setLocalCalendarMeetingId] = useState('');
  const [localSourceConversationId, setLocalSourceConversationId] = useState('');
  const [localSourceNoteId, setLocalSourceNoteId] = useState('');


  // 3️⃣ API Hooks - Queries & Mutations
  const { data: actionTypesData } = useGetActionTypesQuery() 
  const { data: userTrailSessionsData } = useGetUserTrailSessionExecutionsQuery() 
  const { data: sessionsData } = useGetSessionsQuery() 
  const { data: usersData } = useGetUsersQuery();
  const [createNewUserAction] = useCreateNewUserActionMutation()

  const { data: userMissionClientsData } = useGetUserMissionClientsQuery()    // 07 Apr 2025
  const { data: userMissionOrgsData } = useGetUserMissionOrgsQuery()          // 07 Apr 2025
  const { data: userMissionPartnersData } = useGetUserMissionPartnersQuery()  // 07 Apr 2025
  const { data: userMissionSelfsData } = useGetUserMissionSelfsQuery()        // 07 Apr 2025
  const { data: userMissionTeamsData } = useGetUserMissionTeamsQuery()        // 07 Apr 2025
  
  const { data: userVisionClientsData } = useGetUserVisionClientsQuery()      // 07 Apr 2025
  const { data: userVisionOrgsData } = useGetUserVisionOrgsQuery()            // 07 Apr 2025
  const { data: userVisionPartnersData } = useGetUserVisionPartnersQuery()    // 07 Apr 2025
  const { data: userVisionSelfsData } = useGetUserVisionSelfsQuery()          // 07 Apr 2025
  const { data: userVisionTeamsData } = useGetUserVisionTeamsQuery()          // 07 Apr 2025

  const { data: userPlanClientsData } = useGetUserPlanClientsQuery()          // 07 Apr 2025
  const { data: userPlanOrgsData } = useGetUserPlanOrgsQuery()                // 07 Apr 2025
  const { data: userPlanPartnersData } = useGetUserPlanPartnersQuery()        // 07 Apr 2025
  const { data: userPlanSelfsData } = useGetUserPlanSelfsQuery()              // 07 Apr 2025
  const { data: userPlanTeamsData } = useGetUserPlanTeamsQuery()              // 07 Apr 2025


  const extractFilteredUserEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id])
    ?.filter(
        (item) => item?.username === username
        );
  };

  const extractEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id])
  };

  const extractFilteredEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id])
    ?.filter(
        // (item) => item.userId?._id === userId && item?.userOrgId === userOrgId
        (item) => item.userId?._id === userId
        );
  };

  const userInfo = extractFilteredUserEntitiesFromAdapter(usersData)
  
  // 4️⃣ Extract userId and userOrgId from current user
  const userId = userInfo[0]?._id || null
  const userOrgId = userInfo[0]?.userOrgId?._id || null
  // console.log('userId', userId)
  // console.log('userOrgId', userOrgId)

  const actionTypes = extractEntitiesFromAdapter(actionTypesData);
  const sessions = extractEntitiesFromAdapter(sessionsData);
  const userTrailSessions = extractFilteredEntitiesFromAdapter(userTrailSessionsData)

  

  const userMissionClients = extractEntitiesFromAdapter(userMissionClientsData)?.filter(item => item.userId?._id === userId)     //07 Apr 2025
  const userMissionOrgs = extractEntitiesFromAdapter(userMissionOrgsData)?.filter(item => item.userId?._id === userId)           //07 Apr 2025
  const userMissionPartners = extractEntitiesFromAdapter(userMissionPartnersData)?.filter(item => item.userId?._id === userId)   //07 Apr 2025
  const userMissionSelfs = extractEntitiesFromAdapter(userMissionSelfsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025
  const userMissionTeams = extractEntitiesFromAdapter(userMissionTeamsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025
  
  const userVisionClients = extractEntitiesFromAdapter(userVisionClientsData)?.filter(item => item.userId?._id === userId)     //07 Apr 2025
  const userVisionOrgs = extractEntitiesFromAdapter(userVisionOrgsData)?.filter(item => item.userId?._id === userId)           //07 Apr 2025
  const userVisionPartners = extractEntitiesFromAdapter(userVisionPartnersData)?.filter(item => item.userId?._id === userId)   //07 Apr 2025
  const userVisionSelfs = extractEntitiesFromAdapter(userVisionSelfsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025
  const userVisionTeams = extractEntitiesFromAdapter(userVisionTeamsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025

  const userPlanClients = extractEntitiesFromAdapter(userPlanClientsData)?.filter(item => item.userId?._id === userId)     //07 Apr 2025
  const userPlanOrgs = extractEntitiesFromAdapter(userPlanOrgsData)?.filter(item => item.userId?._id === userId)           //07 Apr 2025
  const userPlanPartners = extractEntitiesFromAdapter(userPlanPartnersData)?.filter(item => item.userId?._id === userId)   //07 Apr 2025
  const userPlanSelfs = extractEntitiesFromAdapter(userPlanSelfsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025
  const userPlanTeams = extractEntitiesFromAdapter(userPlanTeamsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025

  // console.log('user vision selfs', userVisionSelfs)
  // console.log('user mission selfs', userMissionSelfs)
  // console.log('user plan selfs', userPlanSelfs)

  // console.log('user vision clients', userVisionClients)
  // console.log('userMissionClientsData', userMissionClientsData)
  // console.log('user mission clients', userMissionClients)
  // console.log('user plan clients', userPlanClients)


  // 07 Apr 2025 - Helper functions for filtered dropdown options

const filteredVisionOptions = (() => {
    switch (selectedFocus) {
      case 'Self': return userVisionSelfs || []
      case 'Client': return userVisionClients || []
      case 'Org': return userVisionOrgs || []
      case 'Team': return userVisionTeams || []
      case 'Partner': return userVisionPartners || []
      default: return []
    }
  })()
  
  const filteredMissionOptions = (() => {
    switch (selectedFocus) {
      case 'Self': return userMissionSelfs?.filter(m => m.visionId?._id === selectedVisionId || m.visionId === selectedVisionId) || []
      case 'Client': return userMissionClients?.filter(m => m.visionId?._id === selectedVisionId || m.visionId === selectedVisionId) || []
      case 'Org': return userMissionOrgs?.filter(m => m.visionId?._id === selectedVisionId || m.visionId === selectedVisionId) || []
      case 'Team': return userMissionTeams?.filter(m => m.visionId?._id === selectedVisionId || m.visionId === selectedVisionId) || []
      case 'Partner': return userMissionPartners?.filter(m => m.visionId?._id === selectedVisionId || m.visionId === selectedVisionId) || []
      default: return []
    }
  })()
  
  const filteredPlanOptions = (() => {
    switch (selectedFocus) {
      case 'Self': return userPlanSelfs?.filter(p => p.missionId?._id === selectedMissionId) || []
      case 'Client': return userPlanClients?.filter(p => p.missionId?._id === selectedMissionId) || []
      case 'Org': return userPlanOrgs?.filter(p => p.missionId?._id === selectedMissionId) || []
      case 'Team': return userPlanTeams?.filter(p => p.missionId?._id === selectedMissionId) || []
      case 'Partner': return userPlanPartners?.filter(p => p.missionId?._id === selectedMissionId) || []
      default: return []
    }
  })()

  // console.log('filteredVisionOptions', filteredVisionOptions)
  // console.log('filteredMissionOptions', filteredMissionOptions)
  // console.log('filteredPlanOptions', filteredPlanOptions)
  
  
  // 5️⃣ Group Sessions by Trail
  const trails = userTrailSessions?.reduce((acc, session) => {
    const trailId = session.trailId?._id;
    if (!acc[trailId]) {
      acc[trailId] = {
        trailId,
        trailName: session.trailId?.trailName || "Unnamed Trail",
        sessions: [],
      };
    }
    acc[trailId].sessions.push({
      sessionId: session?.sessionId?._id,
      sessionName: session.sessionId?.sessionName || "Unnamed Session",
    });
    return acc;
  }, {});
//   console.log('trails', trails)

  // 6️⃣ Convert trails object to array of options
  const trailOptions = Object.values(trails || {});
  // console.log('trailOptions', trailOptions)

  // 7️⃣ Form Submit Handler
  const handleSubmit = async () => {
    // Validation
    if (!selectedTrailId || !selectedSessionId || !selectedActionTypeId || !actionName || !completionDate) {
      alert("Please fill in all fields: Trail, Session,Action Type, Action Name and Completion Date")
      return
    }


    const selectedTrailName = trailOptions.find(t => t.trailId === selectedTrailId)?.trailName // 07 Apr 2025

    // ⛔ Ensure vision is at least selected if trail is MyMissions
    if (
      selectedTrailName === "MyMissions" &&
      selectedFocus &&
      !selectedVisionId &&
      !selectedMissionId &&
      !selectedPlanId
    ) {
      alert("Please select at least a Vision before submitting an action in the MyMissions trail.");
      return;
    }

    const newAction = {
      userId,
      userOrgId,
      trailId: selectedTrailId,
      sessionId: selectedSessionId,
      actionTypeId: selectedActionTypeId,
      userActionName: actionName,
      userActionDesc: actionDesc,
      completeBy: completionDate,
      selectedFocus: selectedFocus, // 08 Apr 2025
    }

    // 07 Apr 2025 - Added MyMissions-specific fields if trail is MyMissions
    if (selectedTrailName === "MyMissions" && selectedFocus) {
  
        if (selectedVisionId) {
          newAction[`actionVision${selectedFocus}Ids`] = [{ visionId: selectedVisionId }]
        }
  
        if (selectedMissionId) {
          newAction[`actionMission${selectedFocus}Ids`] = [{ missionId: selectedMissionId }]
        }
  
        if (selectedPlanId) {
          newAction[`actionPlan${selectedFocus}Ids`] = [{ planId: selectedPlanId }]
        }
      }
    
        // 22 Jun 2025 - Add calendarMeetingId if provided via props or form
        if (calendarMeetingId) {
          newAction.calendarMeetingId = calendarMeetingId;
        } else if (localCalendarMeetingId) {
          newAction.calendarMeetingId = localCalendarMeetingId;
        }

        // 22 Jun 2025 - Add sourceConversation reference if provided via props or form
        if (sourceConversation && sourceConversation?.sourceConversationId && sourceConversation?.sourceNoteId) {
          newAction.sourceConversation = {
            sourceConversationId: sourceConversation.sourceConversationId,
            sourceNoteId: sourceConversation.sourceNoteId
          };
        } else if (localSourceConversationId && localSourceNoteId) {
          newAction.sourceConversation = {
            sourceConversationId: localSourceConversationId,
            sourceNoteId: localSourceNoteId
          };
        }


        



    try {
      await createNewUserAction(newAction).unwrap()
      if (refetchUserActions) refetchUserActions()
      onClose()
    } catch (err) {
      console.error("Error creating action:", err)
      alert("Failed to create action")
    }
  }

  // 8- Handle Cancel 
const handleCancel = () => {
    if (onClose) onClose()
  }


  return (
    <div className="bg-gray-100 m-6 p-4 rounded shadow-lg">
      <p className="text-sm font-bold text-orange-600">Create a new Action</p>

      {/* 8️⃣ Full JSX Form UI */}
        <form className="space-y-3 text-sm text-gray-700 font-poppins" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        
        {/* Trail Selection */}
        <select
            value={selectedTrailId}
            onChange={(e) => {
            setSelectedTrailId(e.target.value)
            setSelectedSessionId("")
            }}
            disabled={!!defaultTrailId} // ✅ 17 May 2025 Disable if default provided
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        >
            <option value="">Select Trail</option>
            {trailOptions?.map(trail => (
            <option key={trail.trailId} value={trail.trailId}>
                {trail.trailName}
            </option>
            ))}
        </select>

        {/* 07 Apr 2025 - Only show Focus dropdown if trail is MyMissions */}
        {selectedTrailId && trailOptions.find(t => t.trailId === selectedTrailId)?.trailName === "MyMissions" && (
        <>
            {/* a) Focus Selector */}
            <select
            value={selectedFocus}
            onChange={(e) => {
                setSelectedFocus(e.target.value)
                setSelectedVisionId('')
                setSelectedMissionId('')
                setSelectedPlanId('')
            }}
            className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
            <option value="">Select Focus (Self, Client...)</option>
            <option value="Self">Self</option>
            <option value="Client">Client</option>
            <option value="Org">Org</option>
            <option value="Team">Team</option>
            <option value="Partner">Partner</option>
            </select>

            {/* b) Vision Dropdown */}
            {selectedFocus && (
            <select
                value={selectedVisionId}
                onChange={(e) => {
                setSelectedVisionId(e.target.value)
                setSelectedMissionId('')
                setSelectedPlanId('')
                }}
                className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
                <option value="">Select Vision</option>
                {filteredVisionOptions?.map((vision) => (
                <option key={vision._id} value={vision._id}>
                    {vision.visionName}
                </option>
                ))}
            </select>
            )}

            {/* c) Mission Dropdown */}
            {selectedVisionId && (
            <select
                value={selectedMissionId}
                onChange={(e) => {
                setSelectedMissionId(e.target.value)
                setSelectedPlanId('')
                }}
                className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
                <option value="">Select Mission</option>
                {filteredMissionOptions?.map((mission) => (
                <option key={mission._id} value={mission._id}>
                    {mission.missionName}
                </option>
                ))}
            </select>
            )}

            {/* d) Plan Dropdown */}
            {selectedMissionId && (
            <select
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
                <option value="">Select Plan</option>
                {filteredPlanOptions?.map((plan) => (
                <option key={plan._id} value={plan._id}>
                    {plan.planName}
                </option>
                ))}
            </select>
            )}
        </>
        )}


        {/* Session Selection */}
        <select
            value={selectedSessionId}
            onChange={(e) => setSelectedSessionId(e.target.value)}
            disabled={!!defaultSessionId || !selectedTrailId}
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        >
            <option value="">Select Session</option>
            {selectedTrailId &&
            trails[selectedTrailId]?.sessions.map(session => (
                <option key={session.sessionId} value={session.sessionId}>
                {session.sessionName}
                </option>
            ))}
        </select>

        {/* Action Type Selection */}
        <select
            value={selectedActionTypeId}
            onChange={(e) => setSelectedActionTypeId(e.target.value)}
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        >
            <option value="">Select Action Type</option>
            {actionTypesData?.ids.map(id => {
            const actionType = actionTypesData.entities[id]
            // console.log('actionType', actionType)
            return (
                <option key={id} value={id}>
                {actionType?.actionTypeName}
                </option>
            )
            })}
        </select>

        {/* Action Name Input */}
        <input
            type="text"
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Enter Action Name"
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        />

        {/* Completion Date */}
        <input
            type="date"
            value={completionDate}
            onChange={(e) => setCompletionDate(e.target.value)}
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        />

        {/* Action Description */}
        <textarea
            value={actionDesc}
            onChange={(e) => setActionDesc(e.target.value)}
            placeholder="Enter Action Description"
            rows={4}
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        />

            <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
                <EtBtnFormBasic
                name={"Create"}
                type={"create"}
                handleClick={handleSubmit}
                />
                <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
                />
            </div>
        </form>

    </div>
  )
}

export default EtCreateFollowUpUserActionComp